import type { ChangeEvent, FunctionComponent } from 'react';
import { useCallback, useState } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { sendErrorEvent } from '@trello/error-reporting';
import { forTemplate } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import { TrashIcon } from '@trello/nachos/icons/trash';
import { Textfield } from '@trello/nachos/textfield';
import { Tooltip } from '@trello/nachos/tooltip';
import type { AppManagementTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { useUpdateAllowedOriginsMutation } from '../queries/UpdateAllowedOriginsMutation.generated';

import styles from './AllowedOrigins.less';

const format = forTemplate('app_management');

export const AllowedOrigins: FunctionComponent<{
  apiKey: string;
  origins: string[];
}> = ({ apiKey, origins }) => {
  const [allowedOrigins, setAllowedOrigins] = useState<string[]>(origins);
  const [url, setUrl] = useState<string>('');
  const [isInvalidUrl, setIsInvalidUrl] = useState<boolean>(false);

  const onUrlChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setUrl(e.target.value);
    },
    [setUrl],
  );

  const taskName = 'edit-plugin/generateApiKey';
  const source = 'powerUpEditScreen';
  const [updateAllowedOrigins] = useUpdateAllowedOriginsMutation();

  const handleAddAllowedOrigin = useCallback(async () => {
    const urlExists = allowedOrigins.includes(url);
    if (urlExists) {
      setUrl('');
    } else if (
      url &&
      /^[a-z][a-z0-9+.-]*:/.test(url) &&
      !/[,&<>'"`\\]/.test(url)
    ) {
      const traceId = Analytics.startTask({
        taskName,
        source,
      });
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const origins = [...allowedOrigins, url];
      setIsInvalidUrl(false);
      try {
        await updateAllowedOrigins({
          variables: {
            keyApplication: apiKey,
            origins,
          },
        });
        setAllowedOrigins(origins);
        setUrl('');
        Analytics.taskSucceeded({
          taskName,
          traceId,
          source,
        });
      } catch (error) {
        Analytics.taskFailed({
          taskName,
          traceId,
          source,
          error,
        });
        sendErrorEvent(error, {
          tags: { ownershipArea: 'trello-web-eng' },
        });
      }
    } else {
      setIsInvalidUrl(true);
    }
  }, [
    url,
    allowedOrigins,
    apiKey,
    updateAllowedOrigins,
    setAllowedOrigins,
    setIsInvalidUrl,
  ]);

  const handleRemoveAllowedOrigin = useCallback(
    (key: number) => async () => {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const origins = allowedOrigins.filter((origin, idx) => idx !== key);
      const traceId = Analytics.startTask({
        taskName,
        source,
      });
      try {
        await updateAllowedOrigins({
          variables: {
            keyApplication: apiKey,
            origins,
          },
        });
        setAllowedOrigins(origins);
        setUrl('');
        Analytics.taskSucceeded({
          taskName,
          traceId,
          source,
        });
      } catch (error) {
        console.error(error);
        Analytics.taskFailed({
          taskName,
          traceId,
          source,
          error,
        });
        sendErrorEvent(error, {
          tags: { ownershipArea: 'trello-web-eng' },
        });
      }
    },
    [allowedOrigins, apiKey, updateAllowedOrigins],
  );

  return (
    <div>
      <div className={styles.formSection}>
        <div>
          <Textfield
            className={styles.textfield}
            label={
              <label className={styles.label}>
                {format('allowed-origins')}
              </label>
            }
            placeholder={format('allowed-origin-example')}
            value={url}
            onChange={onUrlChange}
            iconAfter={
              <Button
                className={styles.addBtn}
                children={format('add')}
                onClick={handleAddAllowedOrigin}
              />
            }
          />
          {isInvalidUrl && (
            <span className={styles.isInvalidUrl}>
              {format('error-invalid-url')}
            </span>
          )}
          <div className={styles.origins}>
            {allowedOrigins.map((value, idx) => (
              <div
                className={styles.origin}
                // eslint-disable-next-line @eslint-react/no-array-index-key -- FIXME
                key={idx}
                data-testid={getTestId<AppManagementTestIds>('allowed-origin')}
              >
                <div>{value}</div>
                <Tooltip
                  content={format('delete-allowed-origin')}
                  position="top"
                >
                  <Button
                    className={styles.copyBtn}
                    children={<TrashIcon size="small" />}
                    onClick={handleRemoveAllowedOrigin(idx)}
                  />
                </Tooltip>
              </div>
            ))}
          </div>
        </div>
        <div
          className={styles.inputDescription}
          // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml -- FIXME
          dangerouslySetInnerHTML={{
            __html: format('allowed-origins-description'),
          }}
        />
      </div>
    </div>
  );
};
